import { computed } from 'vue';
import { useRuntimeConfig } from '#imports';
import useAuthStore, { type NavItem } from '~/stores/auth';
import { LmsRouteNames } from '~/types/types';
import type { NuxtLinkProps } from 'nuxt/app';

const NAV_ROUTE_MAP: Record<string, NuxtLinkProps['to'] | null> = {
   home: { name: LmsRouteNames.home },
   play_learn: { name: LmsRouteNames.lmsPlayLearn },
   e_learning: null,
   levels: { name: LmsRouteNames.byLevel },
   topics_areas: { name: LmsRouteNames.courseCategories },
   all_content: { name: LmsRouteNames.allContent },
   contributors: { name: LmsRouteNames.contributors },
   blog: { name: LmsRouteNames.blogHome },
   access_options: null,
   premium_packages: '/lms/premium',
   premium_subscriptions: '/lms/premium-subscriptions',
   site_wide_subscription: { name: LmsRouteNames.subscribe },
   tournaments: { name: LmsRouteNames.tournaments },
   chukkers: { name: LmsRouteNames.chukkers },
   video_gallery: { name: LmsRouteNames.video },
   image_gallery: { name: LmsRouteNames.gallery },
   memberships: { name: LmsRouteNames.memberships },
   club_listings: { name: LmsRouteNames.clubListings },
   members_portal: 'https://members.uspolo.org/',
};

const EXTERNAL_NAV_KEYS = new Set(['members_portal']);

export function useLmsNavigation() {
   const authStore = useAuthStore();


   const settingsLoaded = computed(
      () => !authStore.isFetchingUserProfile && authStore.user?.user?.email !== '',
   );

   const navItems = computed<NavItem[]>(
      () => authStore?.user?.site_settings?.nav_items ?? [],
   );

   function findNavItem(key: string): NavItem | undefined {
      for (const item of navItems.value) {
         if (item.key === key) {
            return item;
         }
         if (item.children) {
            const child = item.children.find(c => c.key === key);
            if (child) {
               return child;
            }
         }
      }
      return undefined;
   }

   function isNavItemVisible(key: string): boolean {
      const item = findNavItem(key);
      if (!item) {
         return false;
      }
      return item.is_visible;
   }

   function getNavItemLabel(key: string, fallback: string): string {
      const item = findNavItem(key);
      return item?.label || fallback;
   }

   function isExternalLink(key: string): boolean {
      return EXTERNAL_NAV_KEYS.has(key);
   }

   const config = useRuntimeConfig();

   function getNavRoute(key: string): NuxtLinkProps['to'] | null {
      // Polo Ireland's LMS "Home" links to the public homepage
      if (key === 'home' && config.public.portalBrand === 'poloireland') {
         return '/';
      }
      return NAV_ROUTE_MAP[key] ?? null;
   }

   const visibleNavItems = computed<NavItem[]>(() => {
      if (!settingsLoaded.value) {
         return [];
      }
      const items = navItems.value
         .filter(item => item.is_visible)
         .map(item => {
            if (!item.children?.length) {
               return item;
            }
            return {
               ...item,
               children: item.children.filter(child => child.is_visible),
            };
         });

      if (config.public.portalBrand === 'poloireland') {
         items.push({
            key: 'club_listings',
            label: 'Club Listings',
            position: items.length + 1,
            is_visible: true,
         });
      }

      return items;
   });

   return {
      settingsLoaded,
      navItems,
      visibleNavItems,
      findNavItem,
      isNavItemVisible,
      getNavItemLabel,
      isExternalLink,
      getNavRoute,
      NAV_ROUTE_MAP,
   };
}
